import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, Subject } from 'rxjs';
import type { IChatAdminService, IChatAdminConversationsParams } from '@core-interfaces/services/chat-admin';
import type { IPaginatedChatAdminConversations, IChatAdminMessage, IChatReportAdmin } from '@core-interfaces/dtos/chat_admin';
import { Microservice } from '@core-interfaces/global/microservice';
import { ApiSuccessResponse } from '@core-interfaces/global/http';

@Injectable({ providedIn: 'root' })
export class ChatAdminService implements IChatAdminService {
  constructor(private http: HttpClient) {}

  private baseUrl = `/${Microservice.backoffice_service}/chat`;

  private pendingBadgeRefresh = new Subject<void>();
  pendingBadgeRefresh$ = this.pendingBadgeRefresh.asObservable();

  notifyPendingBadgeRefresh(): void {
    this.pendingBadgeRefresh.next();
  }

  getConversations(params?: IChatAdminConversationsParams): Observable<IPaginatedChatAdminConversations> {
    let httpParams = new HttpParams();
    if (params?.page !== undefined) httpParams = httpParams.set('page', params.page.toString());
    if (params?.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString());
    if (params?.request_status) httpParams = httpParams.set('request_status', params.request_status);
    if (params?.client_name) httpParams = httpParams.set('client_name', params.client_name);
    if (params?.provider_name) httpParams = httpParams.set('provider_name', params.provider_name);
    if (params?.report_filter) httpParams = httpParams.set('report_filter', params.report_filter);
    return this.http
      .get<ApiSuccessResponse<IPaginatedChatAdminConversations>>(`${this.baseUrl}/conversations`, { params: httpParams })
      .pipe(map((response) => response.data));
  }

  getMessages(serviceRequestId: number, beforeId?: number, limit?: number): Observable<IChatAdminMessage[]> {
    let httpParams = new HttpParams();
    if (beforeId !== undefined) httpParams = httpParams.set('before_id', beforeId.toString());
    if (limit !== undefined) httpParams = httpParams.set('limit', limit.toString());
    return this.http
      .get<ApiSuccessResponse<IChatAdminMessage[]>>(`${this.baseUrl}/conversations/${serviceRequestId}/messages`, { params: httpParams })
      .pipe(map((response) => response.data));
  }

  getReports(serviceRequestId: number): Observable<IChatReportAdmin[]> {
    return this.http
      .get<ApiSuccessResponse<IChatReportAdmin[]>>(`${this.baseUrl}/conversations/${serviceRequestId}/reports`)
      .pipe(map((response) => response.data));
  }

  markReportReviewed(reportId: number): Observable<IChatReportAdmin | null> {
    return this.http
      .patch<ApiSuccessResponse<IChatReportAdmin | null>>(`${this.baseUrl}/reports/${reportId}/reviewed`, {})
      .pipe(map((response) => response.data));
  }
}
